import { ComponentProps, forwardRef } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { CloseButton } from '../CloseButton';

type ContentProps = ComponentProps<typeof RadixDialog.Content> & {
  overlayClassName?: string;
};

// The Ada chat host is appended to <body> by the remote loader, outside this dialog's DOM tree.
// Radix would read a click on it as an outside interaction and close the dialog.
const isAdaWidgetTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[data-wonderful-host]') !== null;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ children, ...props }, forwardedRef) => {
  const { overlayClassName, onInteractOutside, ...contentProps } = props;

  const handleInteractOutside: ContentProps['onInteractOutside'] = event => {
    if (isAdaWidgetTarget(event.target)) {
      event.preventDefault();
      return;
    }
    onInteractOutside?.(event);
  };

  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={overlayClassName} />
      <RadixDialog.Content {...contentProps} onInteractOutside={handleInteractOutside} ref={forwardedRef}>
        <RadixDialog.Close asChild>
          <CloseButton data-testid="DialogContent_Close_Button" />
        </RadixDialog.Close>
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
});

Content.displayName = 'DialogContent';

export const DialogContent = Content;
